import { useCallback, useEffect, useMemo, useRef } from "react";

import { useDispatch } from "react-redux";

import { startTranscriptionSummaryTask } from "@/redux/transcription-summary";
import type { store as appStore } from "@/store";

import { Transcriber, type TranscriberRef } from "@components/Transcriber";
import type { TextInsertionOptions } from "@components/Transcriber/hooks";
import type { LiveSegment } from "@components/Transcriber/segments";

import type { SpeakerHint } from "@pages/ReportStudio/AudioTranscriber/components/SpeakerChecklist";
import { buildSummarizeFormData } from "@pages/ReportStudio/AudioTranscriber/components/summarize-form-data";
import { useRecordingSession } from "@pages/ReportStudio/AudioTranscriber/hooks/useRecordingSession";

import { useRecordingDock } from "./store/useRecordingDock";
import { useNotesAutosave } from "./useNotesAutosave";

/**
 * Root-mounted owner of the live recording session. The Azure
 * Transcriber + `useRecordingSession` lifecycle live here for the whole app
 * lifetime, so navigating between case tabs never unmounts them; the session is
 * published to the store and the scribe page / floating dock read it. The
 * summarize-on-stop also fires here, so finishing from the dock (scribe tab
 * unmounted) still works.
 */
export const RecordingHost = () => {
  const dispatch = useDispatch<typeof appStore.dispatch>();

  const hostRef = useRef<TranscriberRef>(null);
  const registerTranscriberRef = useRecordingDock((s) => s.registerTranscriberRef);
  const registerSession = useRecordingDock((s) => s.registerSession);
  const endSession = useRecordingDock((s) => s.endSession);
  const setStatus = useRecordingDock((s) => s.setStatus);
  const startClock = useRecordingDock((s) => s.startClock);
  const pauseClock = useRecordingDock((s) => s.pauseClock);
  const registerControls = useRecordingDock((s) => s.registerControls);

  useEffect(() => {
    registerTranscriberRef(hostRef);
    return () => registerTranscriberRef(null);
  }, [registerTranscriberRef]);

  useNotesAutosave();

  // Dispatch the diarized summarize on session end. Reads the owning
  // case/examinee + transcript from the store/host, so it works off-tab.
  const handleRecordingSessionEnd = useCallback(
    (audioBlob: Blob | null, speakerHints: SpeakerHint[]) => {
      const { caseNumber, examineeName } = useRecordingDock.getState();
      const transcript = hostRef.current?.getFullTranscript()?.trim() ?? "";

      if (!audioBlob && !transcript) {
        console.warn(
          "[RecordingHost] No audio blob and no transcript on session end — skipping summarize."
        );
        return;
      }
      if (!caseNumber || !examineeName) {
        // Don't log the examinee name (PHI).
        console.warn(
          "[RecordingHost] Missing case_number or examinee_name — skipping summarize.",
          { hasCaseNumber: !!caseNumber, hasExamineeName: !!examineeName }
        );
        return;
      }

      const formData = buildSummarizeFormData({
        caseNumber,
        examineeName,
        audioBlob,
        fallbackTranscript: transcript,
        speakerHints,
      });
      dispatch(startTranscriptionSummaryTask(formData));
    },
    [dispatch]
  );

  const stopRecordingRef = useRef<(() => void) | null>(null);
  // Tags the audio buffer with the case that owns the session (diagnostic only —
  // cross-session isolation comes from session-id ownership). Read imperatively
  // at claim time: the scribe page calls `beginSession` and `start` in the same
  // tick, so a rendered value would always be stale.
  const getOwningCaseNumber = useCallback(
    () => useRecordingDock.getState().caseNumber,
    []
  );
  const session = useRecordingSession({
    transcriberRef: hostRef,
    onRecordingSessionEnd: handleRecordingSessionEnd,
    stopRecordingRef,
    getOwningCaseNumber,
  });

  // Publish the live session + its imperative controls to the store so the
  // scribe page (live view) and the floating dock can both drive it.
  useEffect(() => {
    registerSession(session);
    registerControls({
      pause: () => session.pause(),
      resume: () => session.resume(),
      stop: () => session.stop(),
    });
  }, [session, registerSession, registerControls]);

  // Project the session status → store (case ownership + clock lifecycle) so
  // the dock renders correctly. The owning case is claimed by the
  // scribe page via beginSession at start; here we just track status + clock.
  const status = session.status;
  // A remount renders once with the initial `idle` before the hook's adoption
  // effect restores the live status, so treating that first `idle` as "session
  // over" would call endSession() and wipe the owning case, examinee and clock
  // out from under a recording that is still running (MDRS-233). Only an idle
  // that follows a non-idle status is a real end.
  const sawNonIdleRef = useRef(false);
  useEffect(() => {
    if (status === "idle") {
      if (sawNonIdleRef.current) {
        sawNonIdleRef.current = false;
        endSession();
      }
      return;
    }
    sawNonIdleRef.current = true;
    setStatus(status);
    if (status === "recording") startClock();
    else if (status === "paused" || status === "stopped") pauseClock();
  }, [status, setStatus, startClock, pauseClock, endSession]);

  // Stable delegating callback — reads the currently-registered live callback
  // imperatively so it never churns the Transcriber's internal callbackRef.
  const callback = useMemo(
    () => ({
      onFocus: () => useRecordingDock.getState().liveCallback?.onFocus?.(),
      onEnd: () => useRecordingDock.getState().liveCallback?.onEnd?.(),
      onTextInsert: (text: string, options: TextInsertionOptions) =>
        useRecordingDock.getState().liveCallback?.onTextInsert(text, options),
      onPartialUpdate: (partial: string) =>
        useRecordingDock.getState().liveCallback?.onPartialUpdate?.(partial),
      onSegmentsUpdate: (segments: LiveSegment[], maxSpeakers?: number) =>
        useRecordingDock
          .getState()
          .liveCallback?.onSegmentsUpdate?.(segments, maxSpeakers),
    }),
    []
  );

  return (
    <div className="hidden" aria-hidden="true">
      <Transcriber ref={hostRef} callback={callback} isPersistentSession />
    </div>
  );
};
